import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import Icon from './Icon';
import { isBrowser, isIOSApp, isSafari, isMobile } from '../util/browser';
import { addAnalyticsEvent } from '../util/analyticsUtils';

// REIS-66: first-visit, dismissible "add to home screen" banner.
// - Android/Chromium: shows a real Install button driven by the deferred
//   `beforeinstallprompt` event stashed in client.js.
// - iOS Safari (incl. iPadOS): Apple blocks programmatic install, so we show
//   "Share -> Add to Home Screen" instructions instead.
// Mobile/tablet only; hidden when already installed or previously dismissed.

const DISMISSED_KEY = 'peatusInstallPromptDismissed';

// REIS-66: detect iOS from the userAgent (which always contains iPhone/iPad/
// iPod on iOS Safari), NOT navigator.platform — recent iOS versions/settings
// don't reliably report "iPhone" there, which kept the banner from ever
// appearing on real devices. iPadOS Safari masquerades as desktop Mac, so also
// treat MacIntel + multi-touch as iOS.
const ua = isBrowser ? navigator.userAgent : '';
const iosLike =
  isBrowser &&
  (/iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const isStandalone = () =>
  isBrowser &&
  (isIOSApp ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches));

const wasDismissed = () => {
  try {
    return isBrowser && window.localStorage.getItem(DISMISSED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const rememberDismissed = () => {
  try {
    window.localStorage.setItem(DISMISSED_KEY, '1');
  } catch (e) {
    // storage disabled (e.g. private mode) — banner just reappears next visit
  }
};

function InstallPrompt({ intl }) {
  // 'android' once a deferred prompt is available, 'ios' for iOS Safari.
  const [platform, setPlatform] = useState(null);
  const [hidden, setHidden] = useState(false);

  // Only phones/tablets, not already installed, not previously dismissed.
  const eligible =
    isBrowser && (isMobile || iosLike) && !isStandalone() && !wasDismissed();

  useEffect(() => {
    if (!eligible) {
      return undefined;
    }
    if (window.deferredInstallPrompt) {
      setPlatform('android');
    } else if (iosLike && isSafari) {
      setPlatform('ios');
    }
    const onReady = () => setPlatform('android');
    const onInstalled = () => setHidden(true);
    window.addEventListener('installpromptready', onReady);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('installpromptready', onReady);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [eligible]);

  const visible = eligible && !hidden && !!platform;

  useEffect(() => {
    if (visible) {
      addAnalyticsEvent({
        event: 'sendMatomoEvent',
        category: 'installprompt',
        action: 'banner',
        name: `shown-${platform}`,
      });
    }
  }, [visible, platform]);

  const close = useCallback(() => {
    rememberDismissed();
    setHidden(true);
    addAnalyticsEvent({
      event: 'sendMatomoEvent',
      category: 'installprompt',
      action: 'banner',
      name: 'dismissed',
    });
  }, []);

  const install = useCallback(() => {
    const deferred = window.deferredInstallPrompt;
    if (!deferred) {
      setHidden(true);
      return;
    }
    deferred.prompt();
    if (deferred.userChoice) {
      deferred.userChoice.then(() => {
        window.deferredInstallPrompt = undefined;
        setHidden(true);
      });
    } else {
      setHidden(true);
    }
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <section className="install-prompt" aria-labelledby="install-prompt-title">
      <span className="install-prompt-app-icon" aria-hidden>
        <Icon img="icon-icon_mapMarker-point" />
      </span>
      <div className="install-prompt-content">
        <p id="install-prompt-title" className="install-prompt-heading">
          <FormattedMessage
            id="install-prompt-heading"
            defaultMessage="Add peatus.ee to your home screen"
          />
        </p>
        <p className="install-prompt-text">
          {platform === 'ios' ? (
            <FormattedMessage
              id="install-prompt-ios-text"
              defaultMessage="Tap Safari’s Share button (the square with an arrow), then “Add to Home Screen”."
            />
          ) : (
            <FormattedMessage
              id="install-prompt-text"
              defaultMessage="Get quick access to peatus.ee straight from your home screen."
            />
          )}
        </p>
      </div>
      {platform === 'android' && (
        <button
          type="button"
          className="install-prompt-install"
          onClick={install}
        >
          <FormattedMessage
            id="install-prompt-install"
            defaultMessage="Install"
          />
        </button>
      )}
      <button
        type="button"
        className="install-prompt-close"
        onClick={close}
        aria-label={intl.formatMessage({
          id: 'install-prompt-close',
          defaultMessage: 'Close',
        })}
      >
        <Icon img="icon-icon_close" />
      </button>
    </section>
  );
}

InstallPrompt.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(InstallPrompt);
